import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from 'v5/platform/dom/cn';

type Layer = { key: string; content: ReactNode; ready: boolean };

const FADE_MS = 300;

export interface CrossfadeLayersProps {
  /** What is shown; a new key fades in over the previous content. */
  layerKey: string;
  children: ReactNode;
  className?: string;
}

/**
 * Crossfades between contents identified by `layerKey`. The previous content
 * stays until the images of the new one have loaded (or failed), so a switch
 * never flashes an empty box; then the new one fades in over it. The first
 * content shows at once. The box takes the size of the content on top.
 *
 * @example
 * ```tsx
 * <CrossfadeLayers layerKey={url ?? 'none'}>
 *   {url ? <img src={url} className="size-full" /> : <Initials />}
 * </CrossfadeLayers>
 * ```
 */
export function CrossfadeLayers({
  layerKey,
  children,
  className
}: CrossfadeLayersProps) {
  const [layers, setLayers] = useState<Layer[]>(() => [
    { key: layerKey, content: children, ready: true }
  ]);
  // What the top layer showed last, kept when a new layer covers it.
  const shown = useRef(children);
  useEffect(() => {
    shown.current = children;
  });

  const stack = stackFor(layers, layerKey, children, shown.current);
  if (stack !== layers) setLayers(stack);

  const topReady = stack[stack.length - 1].ready;
  useEffect(() => {
    if (!topReady || stack.length === 1) return;
    const timeout = setTimeout(
      () => setLayers(current => current.slice(-1)),
      FADE_MS
    );
    return () => clearTimeout(timeout);
  }, [topReady, stack.length]);

  return (
    <div className={cn('relative', className)}>
      {stack.map((layer, index) => {
        const isTop = index === stack.length - 1;
        return (
          <CrossfadeLayer
            key={layer.key}
            ready={layer.ready}
            isTop={isTop}
            onReady={() =>
              setLayers(current =>
                current.map(l =>
                  l.key === layer.key ? { ...l, ready: true } : l
                )
              )
            }
          >
            {isTop ? children : layer.content}
          </CrossfadeLayer>
        );
      })}
    </div>
  );
}

function stackFor(
  layers: Layer[],
  key: string,
  content: ReactNode,
  shownContent: ReactNode
): Layer[] {
  const top = layers[layers.length - 1];
  if (top.key === key) return layers;
  const index = layers.findIndex(layer => layer.key === key);
  // Back to a key still on the stack (A → B → A before B loaded).
  if (index !== -1) return layers.slice(0, index + 1);
  return [
    ...layers.slice(0, -1),
    { ...top, content: shownContent },
    { key, content, ready: false }
  ];
}

function CrossfadeLayer({
  ready,
  isTop,
  onReady,
  children
}: {
  ready: boolean;
  isTop: boolean;
  onReady: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const latestOnReady = useRef(onReady);
  latestOnReady.current = onReady;
  const readyAtMount = useRef(ready);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || readyAtMount.current) return;
    const pending = Array.from(element.querySelectorAll('img')).filter(
      img => !img.complete
    );
    if (pending.length === 0) {
      // Commits the transparent start, or the switch to opaque in this same
      // frame would not transition.
      void getComputedStyle(element).opacity;
      latestOnReady.current();
      return;
    }
    let left = pending.length;
    const settle = () => {
      left -= 1;
      if (left === 0) latestOnReady.current();
    };
    pending.forEach(img => {
      img.addEventListener('load', settle, { once: true });
      img.addEventListener('error', settle, { once: true });
    });
    return () =>
      pending.forEach(img => {
        img.removeEventListener('load', settle);
        img.removeEventListener('error', settle);
      });
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden={!isTop || undefined}
      className={cn(
        // Own stacking context: a layer's negative z-index content stays in it.
        'isolate transition-opacity duration-300 ease-out motion-reduce:transition-none',
        // The top layer sizes the box; the covered ones fill it underneath.
        isTop ? 'relative size-full' : 'pointer-events-none absolute inset-0',
        ready ? 'opacity-100' : 'opacity-0'
      )}
    >
      {children}
    </div>
  );
}
